import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Card, Row, Col, Button, Badge, Tabs, Tab, Table, Form, Spinner } from "react-bootstrap";
import DataTable from "react-data-table-component";
import "../assets/css/dataTable.css";
import { AccountContext } from "components/Account/Account";
import ModalCargando from "components/Modal/ModalCargando";
import ModalError from "components/Modal/ModalError";
import downloadCSV from "../csv/downloadCSV";
import {
  obtenerPanel, obtenerPlanDetalle,
  crearJobDescarga, getJobDescarga,
  crearJobPlan, getJobPlan,
  crearJobAplicar, getJobAplicar,
  reanudarBreaker, setModoBreaker,
} from "api/padronSii";

const AMBIENTES = [
  { valor: "palena", etiqueta: "Producción (palena)", boton: "Ejecutar incremental" },
  { valor: "maullin", etiqueta: "Certificación (maullin)", boton: "Certificación completa" },
];

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
const MAX_INTENTOS = 200; // ~16 minutos a 5s por consulta, igual que CheckDocumentosCargadosDesdeHasta

const RESULTADO_BADGE = {
  ok: "success",
  error: "danger",
  bloqueado_guardas: "warning",
  bloqueado_breaker: "danger",
  pending: "secondary",
  running: "info",
};

function badgeResultado(resultado) {
  const variant = RESULTADO_BADGE[resultado] || "secondary";
  return <Badge bg={variant}>{resultado}</Badge>;
}

function PadronSii() {
  const { getSession } = useContext(AccountContext);

  const [ambiente, setAmbiente] = useState("palena");
  const [panel, setPanel] = useState(null);
  const [cargandoPanel, setCargandoPanel] = useState(false);

  const [filter, setFilter] = useState("");
  const [filaSeleccionada, setFilaSeleccionada] = useState(null);
  const [planDetalle, setPlanDetalle] = useState(null);
  const [tab, setTab] = useState("detalle");

  const [showLoading, setShowLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const cfgAmbiente = useMemo(() => AMBIENTES.find((a) => a.valor === ambiente), [ambiente]);

  const cargarPanel = useCallback(async (amb) => {
    setCargandoPanel(true);
    try {
      const idToken = await getSession().then((s) => s.idToken.jwtToken);
      const resp = await obtenerPanel(idToken, amb);
      setPanel(resp.data);
    } catch (error) {
      console.error(error);
    } finally {
      setCargandoPanel(false);
    }
  }, [getSession]);

  useEffect(() => { cargarPanel(ambiente); setFilaSeleccionada(null); setPlanDetalle(null); }, [ambiente, cargarPanel]);

  // Polling genérico: espera a que un job deje de estar pending/running.
  const esperarJob = async (idToken, getJobFn, jobId, mensajeFn) => {
    for (let i = 0; i < MAX_INTENTOS; i++) {
      await sleep(5000);
      const resp = await getJobFn(idToken, jobId);
      const job = resp.data;
      if (!["pending", "running"].includes(job.status)) return job;
      if (mensajeFn) setLoadingMessage(mensajeFn(job));
    }
    throw new Error("Tiempo de espera agotado.");
  };

  // "Ejecutar incremental" / "Certificación completa": descargar -> plan,
  // orquestado desde el frontend (no una lambda invocando a la otra).
  const ejecutarIncremental = async () => {
    setShowLoading(true);
    setLoadingMessage(" Iniciando descarga...");
    try {
      const idToken = await getSession().then((s) => s.idToken.jwtToken);

      const jobDescarga = await crearJobDescarga(idToken, { ambiente });
      const resultDescarga = await esperarJob(idToken, getJobDescarga, jobDescarga.data.jobId,
        () => " Descargando el padrón del SII...");
      if (resultDescarga.status === "bloqueado_breaker") {
        throw new Error(resultDescarga.error || "Bloqueado por el circuit breaker.");
      }
      if (resultDescarga.status !== "done") {
        throw new Error(resultDescarga.error || "La descarga terminó con error.");
      }

      setLoadingMessage(" Planificando (diff contra la base)...");
      const jobPlan = await crearJobPlan(idToken, { ambiente, csvKey: resultDescarga.resultado.csvKey });
      const resultPlan = await esperarJob(idToken, getJobPlan, jobPlan.data.jobId,
        () => " Planificando (diff contra la base)...");
      if (resultPlan.status === "error") throw new Error(resultPlan.error || "El plan terminó con error.");

      setShowLoading(false);
      await cargarPanel(ambiente);
    } catch (error) {
      setShowLoading(false);
      setErrorMessage(error.message || "Error inesperado.");
      setShowErrorModal(true);
    }
  };

  const aplicarPlan = async (planId, forzar = false) => {
    setShowLoading(true);
    setLoadingMessage(" Aplicando el plan...");
    try {
      const idToken = await getSession().then((s) => s.idToken.jwtToken);
      const job = await crearJobAplicar(idToken, { planId, forzar });
      const resultado = await esperarJob(idToken, getJobAplicar, job.data.jobId, () => " Aplicando el plan...");
      if (resultado.status === "error") throw new Error(resultado.error || "Error al aplicar.");
      setShowLoading(false);
      if (resultado.status === "bloqueado_guardas" && !forzar) {
        setErrorMessage("Hay guardas en rojo. Revisar el detalle antes de forzar la aplicación.");
        setShowErrorModal(true);
      }
      await cargarPanel(ambiente);
      setFilaSeleccionada(null);
      setPlanDetalle(null);
    } catch (error) {
      setShowLoading(false);
      setErrorMessage(error.message || "Error inesperado.");
      setShowErrorModal(true);
    }
  };

  const onReanudar = async () => {
    try {
      const idToken = await getSession().then((s) => s.idToken.jwtToken);
      await reanudarBreaker(idToken, { ambiente });
      await cargarPanel(ambiente);
    } catch (error) {
      setErrorMessage(error.message || "Error al reanudar.");
      setShowErrorModal(true);
    }
  };

  const onToggleModo = async () => {
    const nuevoModo = panel?.breaker?.modo === "manual" ? "automatico" : "manual";
    try {
      const idToken = await getSession().then((s) => s.idToken.jwtToken);
      await setModoBreaker(idToken, { ambiente, modo: nuevoModo });
      await cargarPanel(ambiente);
    } catch (error) {
      setErrorMessage(error.message || "Error al cambiar el modo.");
      setShowErrorModal(true);
    }
  };

  const seleccionarFila = async (fila) => {
    setFilaSeleccionada(fila);
    setPlanDetalle(null);
    setTab("detalle");
    if (!fila.planId) return;
    try {
      const idToken = await getSession().then((s) => s.idToken.jwtToken);
      const resp = await obtenerPlanDetalle(idToken, fila.planId);
      setPlanDetalle(resp.data);
    } catch (error) {
      console.error(error);
    }
  };

  const exportarDetalleCSV = () => {
    if (!planDetalle) return;
    const filas = [
      ...planDetalle.altas.map((f) => ({ accion: "alta", rut: f.rut, razon_social: f.razon_social })),
      ...planDetalle.bajas.map((f) => ({ accion: "baja", rut: f.rut, razon_social: f.razon_social })),
      ...planDetalle.cambios.map((c) => ({ accion: "cambio", rut: c.rut, campos: c.campos.map((x) => x.campo).join("; ") })),
    ];
    if (!filas.length) return;
    downloadCSV(filas, `padron_${planDetalle.plan_id}.csv`);
  };

  const indiceFiltrado = useMemo(() => {
    const indice = panel?.indice || [];
    if (!filter) return indice;
    const f = filter.toLowerCase();
    return indice.filter((x) =>
      (x.tipo || "").toLowerCase().includes(f) ||
      (x.resultado || "").toLowerCase().includes(f) ||
      (x.planId || "").toLowerCase().includes(f));
  }, [panel, filter]);

  const columnas = [
    { name: "Fecha", selector: (row) => row.terminadoAt || row.iniciadoAt, sortable: true, wrap: true,
      cell: (row) => (row.terminadoAt || row.iniciadoAt || "").replace("T", " ").slice(0, 19) },
    { name: "Tipo", selector: (row) => row.tipo, sortable: true,
      cell: (row) => <Badge bg="info" text="dark">{row.tipo}</Badge> },
    { name: "Filas", right: true,
      cell: (row) => row.resumen ? (row.resumen.altas ?? 0) + (row.resumen.bajas ?? 0) + (row.resumen.cambios ?? 0) : "—" },
    { name: "Resultado", cell: (row) => badgeResultado(row.resultado) },
  ];

  const breaker = panel?.breaker;
  const enModoManual = breaker?.modo === "manual";

  return (
    <>
      <Row>
        <Col md="12">
          <Card>
            <Card.Header>
              <Row className="align-items-center">
                <Col>
                  <Card.Title as="h4">Padrón SII</Card.Title>
                  <p className="card-category">
                    {cargandoPanel ? "cargando..." : panel ? `${ambiente} · última descarga ${panel.ultimaDescarga ? panel.ultimaDescarga.descargado_at?.replace("T", " ").slice(0, 16) : "nunca"}` : ""}
                  </p>
                </Col>
                <Col md="4">
                  <Form.Select value={ambiente} onChange={(e) => setAmbiente(e.target.value)}>
                    {AMBIENTES.map((a) => <option key={a.valor} value={a.valor}>{a.etiqueta}</option>)}
                  </Form.Select>
                </Col>
              </Row>
            </Card.Header>
            <Card.Body>
              {enModoManual && (
                <div className="alert alert-danger d-flex justify-content-between align-items-center">
                  <div>
                    <strong>Modo manual activo</strong> — {breaker.errores_consecutivos} errores consecutivos.
                    {breaker.ultimo_error && <div><small>{breaker.ultimo_error.causa}: {breaker.ultimo_error.mensaje}</small></div>}
                  </div>
                  <Button variant="success" size="sm" onClick={onReanudar}>Reanudar automático</Button>
                </div>
              )}

              <Row className="mb-3">
                <Col md="3">
                  <Card body>
                    <div className="text-muted small">CIRCUIT BREAKER</div>
                    <div className="h5">{breaker ? `${breaker.errores_consecutivos}/3 · ${breaker.modo}` : "—"}</div>
                  </Card>
                </Col>
                <Col md="3">
                  <Card body>
                    <div className="text-muted small">ÚLTIMA DESCARGA</div>
                    <div className="h5">{panel?.ultimaDescarga ? panel.ultimaDescarga.descargado_at?.replace("T", " ").slice(0, 16) : "—"}</div>
                  </Card>
                </Col>
                <Col md="3">
                  <Card body>
                    <div className="text-muted small">EJECUCIONES REGISTRADAS</div>
                    <div className="h5">{panel?.indice?.length ?? "—"}</div>
                  </Card>
                </Col>
                <Col md="3">
                  <Card body>
                    <div className="text-muted small">AUTOMÁTICO</div>
                    <Form.Check
                      type="switch"
                      checked={breaker?.modo === "automatico"}
                      onChange={onToggleModo}
                      label={breaker?.modo === "automatico" ? "Prendido" : "Apagado"}
                    />
                  </Card>
                </Col>
              </Row>

              <div className="mb-3">
                <Button variant="primary" onClick={ejecutarIncremental} disabled={showLoading}>
                  {cfgAmbiente.boton}
                </Button>
              </div>

              <Row>
                <Col md="7">
                  <Form.Control
                    type="text" placeholder="Filtrar ejecuciones..." className="mb-2"
                    value={filter} onChange={(e) => setFilter(e.target.value)}
                  />
                  <DataTable
                    className="-striped -highlight"
                    fixedHeader
                    columns={columnas}
                    data={indiceFiltrado}
                    pagination
                    highlightOnHover
                    pointerOnHover
                    onRowClicked={seleccionarFila}
                    progressPending={cargandoPanel}
                  />
                </Col>
                <Col md="5">
                  {!filaSeleccionada && <p className="text-muted">Seleccioná una ejecución para ver el detalle.</p>}
                  {filaSeleccionada && (
                    <Tabs activeKey={tab} onSelect={(k) => setTab(k)} className="mb-2">
                      <Tab eventKey="detalle" title="Detalle">
                        {!planDetalle && filaSeleccionada.planId && <Spinner animation="border" size="sm" />}
                        {!filaSeleccionada.planId && <p className="text-muted small">Esta ejecución no generó un plan (descarga, o error antes de planificar).</p>}
                        {planDetalle && (
                          <>
                            <p className="small text-monospace">{planDetalle.plan_id}</p>
                            <Table size="sm" borderless>
                              <tbody>
                                <tr><td>Altas</td><td>{planDetalle.resumen.altas}</td></tr>
                                <tr><td>Bajas</td><td>{planDetalle.resumen.bajas}</td></tr>
                                <tr><td>Cambios</td><td>{planDetalle.resumen.cambios}</td></tr>
                                <tr><td>Reactivaciones</td><td>{planDetalle.resumen.reactivaciones}</td></tr>
                              </tbody>
                            </Table>
                            <div className="mb-2">
                              {planDetalle.guardas.map((g) => (
                                <Badge key={g.nombre} bg={g.ok ? "success" : "danger"} className="me-1">
                                  {g.nombre}: {g.ok ? "OK" : "ROJO"}
                                </Badge>
                              ))}
                            </div>
                            <Button
                              variant="success" size="sm"
                              onClick={() => aplicarPlan(planDetalle.plan_id, false)}
                              disabled={showLoading}
                            >
                              Aplicar
                            </Button>
                            {planDetalle.guardas.some((g) => !g.ok) && (
                              <Button
                                variant="outline-danger" size="sm" className="ms-2"
                                onClick={() => aplicarPlan(planDetalle.plan_id, true)}
                                disabled={showLoading}
                              >
                                Forzar de todos modos
                              </Button>
                            )}
                          </>
                        )}
                      </Tab>
                      <Tab eventKey="hallazgos" title="Hallazgos">
                        {planDetalle && (
                          <>
                            {planDetalle.cambios.slice(0, 20).map((c, i) => (
                              <div key={i} className="small border-bottom py-1">
                                <strong>{c.rut}</strong>: {c.campos.map((x) => `${x.campo}: ${x.anterior} → ${x.nuevo}`).join(", ")}
                              </div>
                            ))}
                            <Button variant="outline-secondary" size="sm" className="mt-2" onClick={exportarDetalleCSV}>
                              Exportar detalle CSV
                            </Button>
                          </>
                        )}
                      </Tab>
                      <Tab eventKey="receptor" title="Receptor">
                        {planDetalle && <BuscadorReceptor plan={planDetalle} />}
                      </Tab>
                    </Tabs>
                  )}
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <ModalCargando
        modalTitleLoading="Padrón SII"
        showLoading={showLoading}
        loadingDetail={true}
        loadingMessage={loadingMessage}
        handleClose={() => setShowLoading(false)}
      />
      <ModalError
        showTitle="Padrón SII"
        showErrorModal={showErrorModal}
        errorMessage={errorMessage}
        resetModal={() => setShowErrorModal(false)}
      />
    </>
  );
}

// Búsqueda por RUT dentro del plan ya cargado en memoria (no pega a la API de nuevo).
function BuscadorReceptor({ plan }) {
  const [rut, setRut] = useState("");
  const resultado = useMemo(() => {
    if (!rut) return null;
    const n = parseInt(rut, 10);
    return [...plan.altas, ...plan.bajas, ...plan.cambios.map((c) => ({ rut: c.rut, campos: c.campos }))]
      .find((f) => f.rut === n) || null;
  }, [rut, plan]);

  return (
    <>
      <Form.Control type="text" placeholder="RUT (sin DV)" value={rut} onChange={(e) => setRut(e.target.value)} className="mb-2" />
      {rut && !resultado && <p className="text-muted small">No aparece en este plan (sin cambios, o no forma parte del delta).</p>}
      {resultado && <pre className="small">{JSON.stringify(resultado, null, 2)}</pre>}
    </>
  );
}

export default PadronSii;
